import axios from "axios";
import TokenService from "./token.service";
import { logout, getCurrentUser } from '../common/utils';
import { idiomaAtual } from '../i18n';

function refreshToken(){
    return instance.post("/token/refresh/", {
        refresh: TokenService.getLocalRefreshToken(),
    });
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api-hlg.aditushealth.com.br/api/v1/";

const instance = axios.create({
    baseURL: API_BASE_URL
});

instance.interceptors.request.use(
    (config) => {
        const token = TokenService.getLocalAccessToken();
        if (token) {
            config.headers["Authorization"] = 'Bearer ' + token;
        }

        try {
            const currentUser = getCurrentUser();
            if (currentUser && currentUser.acesso_liberado === true && currentUser.ambiente_programa_id) {
                config.headers["X-Programa-ID"] = currentUser.ambiente_programa_id;
            }
        } catch (e) {
            console.error("Erro ao tentar adicionar header X-Programa-ID:", e);
        }

        // Idioma da interface. O backend usa este header (LocaleMiddleware) para
        // responder mensagens de erro e validação no idioma do usuário.
        try {
            config.headers["Accept-Language"] = idiomaAtual();
        } catch (e) {
            console.error("Erro ao tentar adicionar header Accept-Language:", e);
        }

        if (config.data instanceof FormData) {
            delete config.headers["Content-Type"];
        } else {
            config.headers["Content-Type"] = "application/json";
        }

        if (config.method === 'get') {
            const dados_usuario = getCurrentUser();

            if (dados_usuario?.tipo_usuario) {
                // Garante que params exista
                config.params = config.params || {};
                // Só adiciona se ainda não foi passado explicitamente
                if (!config.params.tipo_usuario) {
                    config.params.tipo_usuario = dados_usuario.tipo_usuario;
                }
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Falha passageira: a rede caiu no meio, a requisição estourou o tempo ou o servidor devolveu
 * erro de gateway (o que acontece durante uma publicação). Nesses casos vale repetir.
 *
 * 401/403/404/500 ficam de fora: repetir não muda o resultado e só atrasa a mensagem de erro.
 */
const STATUS_TRANSITORIOS = [502, 503, 504];
const MAX_TENTATIVAS = 2;

function ehFalhaPassageira(err) {
    //requisição cancelada pela própria tela não é falha
    if (err?.code === 'ERR_CANCELED' || err?.message === 'canceled') return false;

    //sem resposta = não chegou ao servidor (rede/timeout)
    if (!err?.response) return true;

    return STATUS_TRANSITORIOS.indexOf(err.response.status) !== -1;
}

instance.interceptors.response.use(
    (res) => {
        try {
            const url = res?.config?.url || '';
            if (url.indexOf('/visitas/dashboards/') !== -1 || url.indexOf('/programas/') === 0 || url.startsWith('programas/')) {
                const debug = {
                    ts: new Date().toISOString(),
                    url,
                    status: res?.status,
                    dataType: Array.isArray(res?.data) ? 'array' : typeof res?.data,
                    keys: res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? Object.keys(res.data) : null,
                    data: res?.data,
                };
                window.__lastDashboardApi = window.__lastDashboardApi || [];
                window.__lastDashboardApi.push(debug);
                if (window.__lastDashboardApi.length > 30) window.__lastDashboardApi.shift();
                try { sessionStorage.setItem('lastDashboardApi', JSON.stringify(window.__lastDashboardApi)); } catch (_) { /* ignore */ }
            }
        } catch (_) { /* ignore */ }
        return res;
    },
    async (err) => {
        const originalConfig = err.config;

        if (originalConfig.url !== "/login" && originalConfig.url !== "/token/refresh/" && err.response && err.response.data) {
            if (err.response.data.code === "token_not_valid") {
                try {
                    const rs = await refreshToken();
                    TokenService.updateLocalAccessToken(rs.data.access);        
                    instance.defaults.headers.common["Authorization"] = 'Bearer '+ rs.data.access;
                    return instance(originalConfig);
                }
                catch (_error) {
                    logout();
                    window.location.href = '/';

                    if (_error.response && _error.response.data) {
                        return Promise.reject(_error.response.data);
                    }
          
                    return Promise.reject(_error);
                }
            }
        }

        /**
         * Repete a consulta quando a falha é passageira. Só GET: repetir uma escrita
         * duplicaria o lançamento (uma despesa cadastrada duas vezes, por exemplo), e o
         * usuário não teria como saber. A agenda e as listagens deixavam de exibir os dados
         * quando a chamada falhava no meio de uma publicação.
         */
        const metodo = (originalConfig?.method || 'get').toLowerCase();

        if (originalConfig && metodo === 'get' && ehFalhaPassageira(err)) {
            originalConfig.__tentativas = (originalConfig.__tentativas || 0) + 1;

            if (originalConfig.__tentativas <= MAX_TENTATIVAS) {
                //espera um pouco mais a cada tentativa, para não insistir em cima de um servidor ocupado
                await new Promise(resolve => setTimeout(resolve, 800 * originalConfig.__tentativas));

                return instance(originalConfig);
            }
        }

        return Promise.reject(err);
    }
);

export default instance;
